import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetFileQuery, useDeleteFileMutation } from "store/queries/chat";
import styled from "styled-components";

const DetailContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  margin-bottom: 20px;
`;

const Label = styled.div`
  font-weight: 600;
  color: #374151;
`;

const Value = styled.div`
  color: #1f2937;
  word-break: break-all;
`;

const ContentSection = styled.div`
  margin-top: 24px;
`;

const ContentBox = styled.pre`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  font-family: "Monaco", "Menlo", "Courier New", monospace;
  font-size: 14px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

export function FileDetailPage() {
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const { current: toastElement } = useContext(ToastContext);
  const { data: fileData, isLoading } = useGetFileQuery(fileId!);
  const [deleteFile] = useDeleteFileMutation();

  const handleDelete = async () => {
    try {
      await deleteFile(fileId!);
      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "File has been deleted successfully.",
      });
      navigate("/chatbot/files");
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Error while deleting file.",
      });
    }
  };

  const handleBack = () => {
    navigate("/chatbot/files");
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!fileData) {
    return (
      <DetailContainer>
        <Card title="File Not Found">
          <p>The requested file could not be found.</p>
          <Button label="Back to Files" onClick={handleBack} />
        </Card>
      </DetailContainer>
    );
  }

  const { db: dbFile, openAi: openAiFile } = fileData;

  return (
    <DetailContainer>
      <Card title="File Details">
        <InfoGrid>
          <Label>File ID:</Label>
          <Value>{dbFile.fileId}</Value>

          <Label>Name:</Label>
          <Value>{dbFile.name}</Value>

          <Label>Identifier:</Label>
          <Value>{dbFile.identifier}</Value>

          <Label>Purpose:</Label>
          <Value>{dbFile.purpose}</Value>

          <Label>Created:</Label>
          <Value>{new Date(dbFile.createdAt).toLocaleString()}</Value>

          <Label>Updated:</Label>
          <Value>{new Date(dbFile.updatedAt).toLocaleString()}</Value>

          <Label>OpenAI Status:</Label>
          <Value>{openAiFile?.status || "Unknown"}</Value>

          <Label>OpenAI Bytes:</Label>
          <Value>{openAiFile?.bytes || "Unknown"}</Value>
        </InfoGrid>

        {dbFile.content && (
          <ContentSection>
            <Label style={{ marginBottom: "12px", display: "block" }}>
              File Content:
            </Label>
            <ContentBox>{dbFile.content}</ContentBox>
          </ContentSection>
        )}

        <ButtonContainer>
          <Button
            label="Back to Files"
            className="p-button-outlined"
            onClick={handleBack}
          />
          <Button
            label="Edit File"
            className="p-button-info"
            onClick={() => navigate(`/chatbot/files/${fileId}/edit`)}
            disabled={
              !dbFile.content ||
              dbFile.identifier === "EligibilityProcessingGuide" ||
              dbFile.identifier === "PricingAdjustmentsGuide" ||
              dbFile.identifier === "ContractManagementGuide"
            }
          />
          <Button
            label="Delete File"
            className="p-button-danger"
            onClick={() => {
              confirmDialog({
                header: "Confirmation",
                icon: "pi pi-exclamation-triangle",
                message: `Are you sure you want to delete "${dbFile.name}"?`,
                accept: handleDelete,
              });
            }}
          />
        </ButtonContainer>
      </Card>
    </DetailContainer>
  );
}
